import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Star, CheckCircle, XCircle, Clock } from 'lucide-react';
import { candidateAPI } from '../../../services';

// Shared dashboard for the Manager and HOD interview rounds. Per the process
// rules, this dashboard ONLY lets the interviewer submit feedback + a
// Selected/Not Selected decision - there is no scheduling or move-forward
// control anywhere here. TA owns all of that.
const InterviewerDashboard = ({ roundType }) => {
  const isHOD = roundType === 'hod';
  const reviewStage = isHOD ? 'hod_review' : 'manager_review';
  const selectedStage = isHOD ? 'hod_selected' : 'manager_selected';
  const rejectedStage = isHOD ? 'hod_rejected' : 'manager_rejected';
  const roundLabel = isHOD ? 'HOD' : 'Manager';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [queue, setQueue] = useState([]);
  const [selectedList, setSelectedList] = useState([]);
  const [rejectedList, setRejectedList] = useState([]);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [feedbackData, setFeedbackData] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [queueRes, selectedRes, rejectedRes] = await Promise.all([
        candidateAPI.getCandidates({ stage: reviewStage, assignedInterviewer: 'me' }),
        candidateAPI.getCandidates({ stage: selectedStage, assignedInterviewer: 'me' }),
        candidateAPI.getCandidates({ stage: rejectedStage, assignedInterviewer: 'me' })
      ]);
      setQueue(queueRes.data?.candidates || []);
      setSelectedList(selectedRes.data?.candidates || []);
      setRejectedList(rejectedRes.data?.candidates || []);
    } catch (err) {
      setError(err.message || 'Failed to load your interview queue');
    } finally {
      setLoading(false);
    }
  }, [reviewStage, selectedStage, rejectedStage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const managerFeedbackFor = (candidate) => {
    const managerInterview = [...(candidate.interviews || [])].reverse().find(i => i.roundType === 'manager' && i.feedback?.decision);
    return managerInterview?.feedback;
  };

  const latestInterviewFor = (candidate) => {
    return [...(candidate.interviews || [])].reverse().find(i => i.roundType === roundType);
  };

  const submitFeedback = async (decision) => {
    if (!feedbackTarget) return;
    try {
      setSubmitting(true);
      await candidateAPI.submitLatestInterviewFeedback(feedbackTarget._id, {
        rating: feedbackData.rating ? parseInt(feedbackData.rating, 10) : undefined,
        comments: feedbackData.comments,
        decision
      });
      setFeedbackTarget(null);
      setFeedbackData({});
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{roundLabel} Interview Dashboard</h1>
        <p className="text-gray-500 mt-1">Review candidates assigned to you and submit your decision.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {/* Interview Queue */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">Interview Queue ({queue.length})</h3>
        {queue.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No candidates awaiting your feedback</p>
        ) : (
          <div className="space-y-3">
            {queue.map(candidate => {
              const interview = latestInterviewFor(candidate);
              const priorFeedback = isHOD ? managerFeedbackFor(candidate) : null;
              return (
                <div key={candidate._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                      <p className="text-sm text-gray-500">
                        {candidate.job?.title} • {candidate.experience?.years || 0} yrs exp
                      </p>
                      {candidate.skills?.length > 0 && (
                        <p className="text-xs text-gray-400 mt-1">{candidate.skills.join(', ')}</p>
                      )}
                      {candidate.resume?.url && (
                        <a href={candidate.resume.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                          View Resume
                        </a>
                      )}
                      {interview?.scheduledAt && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                          <Calendar className="w-3 h-3" />
                          {new Date(interview.scheduledAt).toLocaleString('en-IN')}
                        </div>
                      )}
                      {priorFeedback && (
                        <div className="mt-2 text-xs bg-purple-50 rounded p-2 text-purple-800">
                          Manager feedback: {priorFeedback.rating}/5 - {priorFeedback.comments}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => { setFeedbackTarget(candidate); setFeedbackData({}); }}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Give Feedback
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected / Rejected read-only lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" /> Selected ({selectedList.length})
          </h3>
          {selectedList.length === 0 ? (
            <p className="text-gray-400 text-sm">None yet</p>
          ) : (
            <div className="space-y-2">
              {selectedList.map(c => (
                <div key={c._id} className="text-sm p-2 bg-green-50 rounded flex items-center gap-2">
                  <Clock className="w-3 h-3 text-green-600" />
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-gray-500">- waiting for TA to {isHOD ? 'move to CTC' : 'schedule HOD'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-600" /> Rejected ({rejectedList.length})
          </h3>
          {rejectedList.length === 0 ? (
            <p className="text-gray-400 text-sm">None yet</p>
          ) : (
            <div className="space-y-2">
              {rejectedList.map(c => (
                <div key={c._id} className="text-sm p-2 bg-red-50 rounded">
                  <span className="font-medium">{c.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {feedbackTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setFeedbackTarget(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4" onClick={e => e.stopPropagation()}>
            <h4 className="font-semibold text-gray-900 mb-3">Feedback for {feedbackTarget.name}</h4>

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                  <Star className="w-3 h-3" /> Rating (1-5)
                </label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={feedbackData.rating || ''}
                  onChange={(e) => setFeedbackData({ ...feedbackData, rating: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={3}
                  value={feedbackData.comments || ''}
                  onChange={(e) => setFeedbackData({ ...feedbackData, comments: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => submitFeedback('selected')}
                disabled={submitting}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
              >
                Selected
              </button>
              <button
                onClick={() => submitFeedback('not_selected')}
                disabled={submitting}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                Not Selected
              </button>
            </div>
            <button
              onClick={() => setFeedbackTarget(null)}
              className="w-full mt-2 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewerDashboard;
